import type {
  DesktopNotificationContent,
  DesktopNotificationContentInput,
} from './index.types';
import { isPhoneNumberString } from 'Modules/softphone/utils/validation';

const TITLE_INCOMING_CALL = 'New incoming call';
const TITLE_VIDEO_CONFERENCE = 'New video conference';


export function getDesktopNotificationContent(
  input: DesktopNotificationContentInput
): DesktopNotificationContent {
  return input.isVideo
    ? getVideoConferenceContent(input)
    : getInboundCallContent(input);
}

function getVideoConferenceContent({
  displayName,
  isGroupConference,
  groupName,
}: Extract<
  DesktopNotificationContentInput,
  { isVideo: true }
>): DesktopNotificationContent {
  const caller = displayName || 'Someone';

  const body =
    isGroupConference && groupName
      ? `${caller} Would like you to join a video conference via ${groupName}`
      : `${caller} Would like you to join a video conference`;

  return { title: TITLE_VIDEO_CONFERENCE, body };
}

function getInboundCallContent({
  displayName,
  phoneNumber,
  location,
  ringGroupName,
  isInternal,
}: Extract<
  DesktopNotificationContentInput,
  { isVideo?: false }
>): DesktopNotificationContent {
  // SIP headers sometimes send a phone number as the display name/location -
  // treat that as "no name" rather than showing an unformatted number as one.
  const effectiveLocation = isPhoneNumberString(location)
    ? undefined
    : location;
  const nameOrLocation = displayName || effectiveLocation;

  // Remote-Party-ID/P-Asserted-Identity (used as ring-group signals upstream
  // in extractRingGroupName) are sent by most PBXs on every call, not just
  // ring-group ones, and often just echo the caller's own identity. When that
  // happens, ringGroupName ends up equal to the caller's own name - treat
  // that as "no ring group" rather than showing "X is calling you via X".
  const effectiveRingGroupName =
    ringGroupName &&
    ringGroupName.trim().toLowerCase() !== (nameOrLocation || '').trim().toLowerCase()
      ? ringGroupName
      : undefined;

  // Ring group info is orthogonal to internal/external caller identification -
  // when present it always wins.
  if (effectiveRingGroupName) {
    const caller = nameOrLocation || phoneNumber || 'Someone';
    return {
      title: TITLE_INCOMING_CALL,
      body: `${caller} is calling you via ${effectiveRingGroupName}`,
    };
  }

  // Internal call: name only, no phone number line.
  if (nameOrLocation && isInternal) {
    return {
      title: TITLE_INCOMING_CALL,
      body: `There is an incoming call from ${nameOrLocation}`,
    };
  }

  // External call, saved contact (or a SIP-provided location string): name/
  // location plus the phone number on a second line.
  if (nameOrLocation && !isInternal) {
    return {
      title: TITLE_INCOMING_CALL,
      body: phoneNumber
        ? `There is an incoming call from ${nameOrLocation}\r\n${phoneNumber}`
        : `There is an incoming call from ${nameOrLocation}`,
    };
  }

  // External call, no name/contact match: phone number only.
  return {
    title: TITLE_INCOMING_CALL,
    body: `There is an incoming call from ${phoneNumber || ''}`,
  };
}
